<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { PageHeader, PageFooter } from '@/components/clients/common';
import { useStateStore } from '../../../stores/state.store';
import { useGKSStore } from '../../../stores/gks/gks.store';
import { DataService } from '../../../services';
import { Logger } from '../../../middlewares/sentry';

const stateStore = useStateStore();
const gksStore = useGKSStore();

const mainColor = ref(stateStore.getColors.mainColor);
const backgroundColor = ref(stateStore.getColors.backgroundColor);
const hiddenGuests = ref(gksStore.getCommonData.hiddenGuests);

const name = ref('');
const tableData = ref({});
const searchData = ref({});

const tableToShow = computed(() => {
  return name.value === '' ? tableData.value : searchData.value;
});

const isFoundAnyPerson = computed(() => {
  return name.value !== '' && Object.keys(searchData.value).length === 0;
});

const isOnePersonVisible = computed(() => {
  return (
    name.value !== '' &&
    Object.keys(searchData.value).length === 1 &&
    Object.values(searchData.value)[0].length === 1
  );
});

const isFoundPersonWithSameData = computed(() => {
  const isNameAndSurname = name.value.trim().split(' ').length > 1;
  if (
    name.value === '' ||
    !isNameAndSurname ||
    isFoundAnyPerson.value ||
    isOnePersonVisible.value
  ) {
    return false;
  }

  let fullNameFound = '';
  for (const peopleInTable of Object.values(searchData.value)) {
    for (const person of peopleInTable) {
      const fullName = `${person.name.split(' ')[0]} ${person.surname.split(' ')[0]}`;

      if (fullNameFound === '') {
        fullNameFound = fullName;
        continue;
      }

      if (
        fullNameFound !== fullName &&
        fullNameFound.includes(fullName) &&
        fullName.includes(fullNameFound)
      ) {
        return false;
      }
    }
  }

  return true;
});

const nameToShow = (person) => {
  const tablesNumber = Object.keys(searchData.value);
  const normalizedData = removePolishDiacritics(name.value.toLocaleLowerCase());
  const searchTerms = normalizedData.split(' ');
  const isTypedPerson =
    (searchTerms[0] === person.name && searchTerms[1] === person.surname) ||
    (searchTerms[1] === person.name && searchTerms[0] === person.surname);
  if (
    (tablesNumber.length === 1 &&
      searchData.value[tablesNumber].length === 1) ||
    isTypedPerson
  ) {
    return person.name;
  }

  return person.hiddenName;
};

function removePolishDiacritics(str) {
  const diacriticsMap = {
    ą: 'a',
    ć: 'c',
    ę: 'e',
    ł: 'l',
    ń: 'n',
    ó: 'o',
    ś: 's',
    ź: 'z',
    ż: 'z',
    Ą: 'A',
    Ć: 'C',
    Ę: 'E',
    Ł: 'L',
    Ń: 'N',
    Ó: 'O',
    Ś: 'S',
    Ź: 'Z',
    Ż: 'Z',
  };
  return str.replace(/[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/g, (match) => diacriticsMap[match]);
}

watch(name, (newValue) => {
  const normalizedData = removePolishDiacritics(newValue.toLocaleLowerCase());
  const searchTerms = normalizedData.split(' ');

  for (const key in searchData.value) {
    if (Object.hasOwnProperty.call(searchData.value, key)) {
      delete searchData.value[key];
    }
  }

  for (const [tableNumber, guests] of Object.entries(tableData.value)) {
    const filteredGuests = guests.filter((person) => {
      const nameParts = removePolishDiacritics(person.name.toLowerCase()).split(
        ' ',
      );
      const surnameParts = removePolishDiacritics(
        person.surname.toLowerCase(),
      ).split(' ');

      const fullNameParts = [...nameParts, ...surnameParts];

      return searchTerms.every((term) =>
        fullNameParts.some((part) => part.startsWith(term)),
      );
    });

    if (filteredGuests.length > 0) {
      searchData.value[tableNumber] = filteredGuests;
    }
  }
});

function pareData(data) {
  const filteredData = data.filter((item) => item.tableNumber >= 0);

  filteredData.sort((a, b) => a.tableNumber - b.tableNumber);

  const parsedData = filteredData.reduce((acc, item) => {
    const { tableNumber, ...rest } = item;
    rest.hiddenName = rest.name[0] + '.';
    acc[tableNumber] = acc[tableNumber] || [];
    acc[tableNumber].push(rest);
    return acc;
  }, {});

  return parsedData;
}

onMounted(async () => {
  try {
    tableData.value = pareData(await DataService.getGuestPlacesToSit());
  } catch (err) {
    Logger.error(err);
  }
});
</script>

<template>
  <div class="content">
    <PageHeader
      :main-color="mainColor"
      :background-color="backgroundColor"
      :header-text="$t('appCore.apps.gks.header')"
    />
    <div class="gks">
      <transition name="fade" mode="out-in">
        <h2 v-show="name === ''" class="find-text">
          {{ $t('appCore.apps.gks.foundPlace') }}
        </h2>
      </transition>
      <input
        v-model="name"
        class="core-default-input-form input-gks"
        :placeholder="$t('appCore.apps.gks.namePlaceholder')"
      />
      <p v-if="isFoundAnyPerson" class="not-found-text">
        {{ $t('appCore.apps.gks.personNotFound') }}
      </p>
      <p
        v-if="hiddenGuests && isFoundPersonWithSameData"
        class="found-multi-people"
      >
        {{ $t('appCore.apps.gks.samePersonFound') }}
      </p>
      <div v-if="!hiddenGuests || isOnePersonVisible" class="list">
        <div
          v-for="(table, tableNumber) in tableToShow"
          :key="tableNumber"
          class="table"
        >
          <h3 class="table-info">
            {{ $t('appCore.apps.gks.table') }} {{ tableNumber }}
          </h3>
          <ol class="guest-list">
            <li v-for="(person, index) in table" :key="index" class="guest">
              {{ nameToShow(person) }} {{ person.surname }}
              <span v-if="isOnePersonVisible && person.tableAdditionalInfo">
                - {{ person.tableAdditionalInfo }}</span
              >
            </li>
          </ol>
        </div>
      </div>
      <p class="footer-text aba">
        {{ $t('appCore.apps.gks.footer') }}
      </p>
    </div>
    <PageFooter />
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styleConstans';

.content {
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: v-bind(backgroundColor);
}

.gks {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  gap: 45px;
  width: 80%;
  height: auto;
  margin: 0 auto;
  align-items: stretch;
  justify-content: center;
  letter-spacing: 2px;
  padding-top: 185px;
  padding-bottom: 135px;
  color: v-bind(mainColor);

  @media screen and (max-width: $media-phone) {
    padding-top: 155px;
  }

  .find-text {
    font-size: 3.3em;
    text-align: center;

    @media screen and (max-width: $media-phone) {
      font-size: 2em;
      line-height: 1.3em;
      font-weight: 500;
    }
  }

  .input-gks {
    max-height: 10px;
    border-color: v-bind(mainColor);
    background-color: #fff;
    color: #000;
  }

  .not-found-text {
    font-size: 3em;
    font-weight: 700;
    text-align: center;
    width: 100%;

    @media screen and (max-width: $media-phone) {
      font-size: 1.9em;
      line-height: 1.5em;
    }
  }

  .found-multi-people {
    font-size: 2.7em;
    font-weight: 700;
    text-align: center;
    width: 100%;

    @media screen and (max-width: $media-phone) {
      font-size: 1.7em;
      line-height: 1.5em;
    }
  }

  .list {
    display: flex;
    flex-flow: row wrap;
    flex: 1 0 auto;
    justify-content: center;
    align-items: flex-start;
    gap: 25px 15px;

    .table {
      flex: 1 0 auto;
      max-width: 70%;
      display: flex;
      flex-direction: column;
      align-items: center;
      width: auto;
      gap: 20px;
      border: 2px solid v-bind(mainColor);
      border-radius: 20px;
      padding: 15px 25px;

      @media screen and (max-width: $media-phone) {
        max-width: 100%;
      }

      .table-info {
        width: 100%;
        text-align: center;
        padding-bottom: 5px;
        font-size: 2.8em;
        border-bottom: 1.5px solid v-bind(mainColor);

        @media screen and (max-width: $media-phone) {
          font-size: 2em;
          line-height: 1.4em;
        }
      }

      .guest-list {
        list-style-position: inside;
        width: 100%;
        text-align: start;
        display: flex;
        flex-direction: column;
        gap: 7px;
      }

      .guest {
        font-size: 1.5em;
        line-height: 1.2em;

        @media screen and (max-width: $media-phone) {
          font-size: 1.3em;
          line-height: 1.1em;
        }
      }
    }
  }
}

.footer-text {
  font-size: 2em;
  color: v-bind(mainColor);
  text-align: center;

  @media screen and (max-width: $media-phone) {
    font-size: 1.6em;
    line-height: 1.4em;
    font-weight: 500;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
